import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UploadBox } from "./upload/UploadBox";
import { FileList } from "./upload/FileList";
import { UploadWarning } from "./upload/UploadWarning";

export const DocumentUpload = () => {
  const { toast } = useToast();
  const { user } = useAuth();
  const [isDragging, setIsDragging] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState<string>("");

  const documentTypes = [
    { id: "id-document", name: "ID Document" },
    { id: "proof-of-residence", name: "Proof of Residence" },
    { id: "fica", name: "FICA" },
    { id: "payslip", name: "Payslip" },
    { id: "bank-statements", name: "Bank Statements (3 Months)" },
  ];

  useEffect(() => {
    const handleFilesSelected = (event: Event) => {
      const customEvent = event as CustomEvent<File[]>;
      handleFiles(customEvent.detail);
    };

    window.addEventListener('filesSelected', handleFilesSelected as EventListener);
    return () => {
      window.removeEventListener('filesSelected', handleFilesSelected as EventListener);
    };
  }, []);

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setIsDragging(true);
    } else if (e.type === "dragleave") {
      setIsDragging(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    handleFiles(droppedFiles);
  };

  const handleFiles = (newFiles: File[]) => {
    setFiles((prev) => [...prev, ...newFiles]);
    toast({
      title: "Files Added",
      description: `${newFiles.length} file(s) added successfully.`,
    });
  };

  const uploadFiles = async () => {
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to upload files.",
        variant: "destructive",
      });
      return;
    }

    if (!selectedDocType) {
      toast({
        title: "Document Type Required",
        description: "Please select a document type before uploading.",
      });
      return;
    }

    setIsUploading(true);
    
    try {
      for (const file of files) {
        const fileExt = file.name.split('.').pop();
        const fileName = `${crypto.randomUUID()}.${fileExt}`;
        const filePath = `${user.id}/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('documents')
          .upload(filePath, file);

        if (uploadError) throw uploadError;

        const selectedType = documentTypes.find(type => type.id === selectedDocType);
        const documentName = `${selectedType?.name} - ${file.name}`;

        const { error: dbError } = await supabase
          .from('documents')
          .insert({
            name: documentName,
            size: file.size,
            type: file.type,
            path: filePath,
            user_id: user.id
          });

        if (dbError) throw dbError;
      }

      toast({
        title: "Success",
        description: "Files uploaded successfully!",
      });

      // Trigger a custom event to refresh the DocumentChecklist
      window.dispatchEvent(new Event('documentUploaded'));

      setFiles([]);
      setSelectedDocType("");
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Error",
        description: "Failed to upload files. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Card className="glass-card p-6 space-y-6 max-w-md w-full mx-auto animate-in">
      <div className="space-y-2">
        <h2 className="text-2xl font-semibold tracking-tight">Upload Documents</h2>
        <p className="text-sm text-muted-foreground">
          First select the document type, then upload the corresponding file
        </p>
      </div>

      <UploadWarning />

      <Select value={selectedDocType} onValueChange={setSelectedDocType}>
        <SelectTrigger>
          <SelectValue placeholder="What document are you uploading?" />
        </SelectTrigger>
        <SelectContent>
          {documentTypes.map((type) => (
            <SelectItem key={type.id} value={type.id}>
              {type.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedDocType && (
        <UploadBox
          isDragging={isDragging}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
        />
      )}

      <FileList
        files={files}
        isUploading={isUploading}
        onUpload={uploadFiles}
      />
    </Card>
  );
};